import { useMemo } from "react";
import { Formik, Form, FormikProps, FormikHelpers } from "formik";
import { FormikTextField, FormikSelectField } from "../../../MUI5FormikComponents/FormikWrappers";
import { Button, ButtonGroup, Grid, Typography, useTheme } from "@mui/material";
import { ICustomer, ICustomerType } from "@vmosolutions/oc-interfaces";
import * as Yup from "yup";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { CustomFieldsV2, computeCustomFieldErrors, sanitizeCustomValues } from "src/components/Custom/CustomFieldsV2";
import { unflattenObj } from "src/utils/index";
import { PolicyResource } from "@vmosolutions/oc-interfaces";
import { FormikSwitchField } from "src/components/MUI5FormikComponents/FormikWrappers";
import _ from "lodash";

interface ICustomersFormProps {
  selectedCustomer: ICustomer;
  className?: string;
  updateCustomerList: (customer: ICustomer, mode: "create" | "update" | "delete") => void;
  customerTypeOptions: ICustomerType[];
  setActiveModal: (activeModal: IActiveModal | null) => void;
}
export const initialCustomerValues: ICustomer = {
  _id: undefined,
  name: "",
  details: "",
  customerType: null,
  active: true,
  custom: {},
};
type CustomersFormType = FormikProps<ICustomer>;

const CustomerSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  details: Yup.string().required("Details is required"),
  customerType: Yup.object()
    .required("Customer Type is required")
    .test("is-valid-customer-type", "Please select a valid Customer Type", (value: any) => {
      return !!value?._id;
    }),
});

export const CustomerForm = (props: ICustomersFormProps): JSX.Element => {
  const { selectedCustomer, className, updateCustomerList, customerTypeOptions, setActiveModal } = props;
  const dispatch = useDispatch();

  const customSchema = useSelector((state: IRootState) => state.customPages?.customer);
  const schema = useMemo(() => {
    try {
      const data = customSchema ? JSON.parse(customSchema) : null;
      return data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [customSchema]);

  const customPath = "custom";
  const buildDefaultsFromSchema = (schema: any): any => {
    if (!schema || !schema.properties) return {};

    const obj: any = {};

    Object.entries(schema.properties).forEach(([key, field]: any) => {
      if (field.type === "object" && field.properties) {
        obj[key] = buildDefaultsFromSchema(field);
        return;
      }

      obj[key] = field.default ?? field?.selectorProps?.defaultValue ?? (field.type === "boolean" ? false : "");
    });

    return obj;
  };

  const initialCustomValues = useMemo(() => {
    if (!schema || !selectedCustomer) return {};
    return buildDefaultsFromSchema(schema);
  }, [schema, selectedCustomer]);

  const initialValues = useMemo(
    () => _.merge({}, { [customPath]: initialCustomValues }, initialCustomerValues, selectedCustomer),
    [selectedCustomer, initialCustomValues, customPath]
  );

  const handleSubmit = async (rawValues: ICustomer, helpers: FormikHelpers<ICustomer>): Promise<void> => {
    const { setSubmitting, setStatus, resetForm } = helpers;
    // Tidy the custom values before saving. The email fields accept spaces and line
    // breaks so a long list can be typed readably, but what gets stored is always the
    // clean "a@x.com;b@y.com" form, because the send side splits on ";" and does not
    // trim. See sanitizeCustomValues.
    const values: ICustomer = {
      ...rawValues,
      [customPath]: sanitizeCustomValues(schema, _.get(rawValues, customPath)),
    };
    const mode = values._id ? "update" : "create";
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: `${mode === "update" ? "Updating" : "Creating New"} Customer...`,
      successMessage: `Customer Successfully ${mode === "update" ? "Updated" : "Created"}.`,
      errorMessage: `Error: Unable to ${mode === "update" ? "Update" : "Create"} Customer`,
    };
    const response =
      mode === "create"
        ? await AmplifyAPIWrapper.Post<ICustomer>("/customer", values, notifications)
        : await AmplifyAPIWrapper.Put<ICustomer>(`/customer/${values._id}`, values, notifications);
    if (response?._id) {
      resetForm({ values });
      setStatus({ success: true });
      // add or update customer from main list.
      updateCustomerList(response, mode);
    } else {
      setStatus({ success: false });
    }
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      validationSchema={CustomerSchema}
      // Custom field errors have to come from Formik's own validation pass, otherwise the
      // pass on submit overwrites them and an invalid value saves anyway. `required` is
      // left out on purpose: a hidden or read only field the user cannot reach must never
      // be able to lock the save button.
      validate={(values) =>
        unflattenObj(
          computeCustomFieldErrors(schema, _.get(values, customPath), customPath, { ignoreKeywords: ["required"] })
        )
      }
    >
      {(customerForm: CustomersFormType) => {
        const { values, dirty, isValid, isSubmitting, handleReset } = customerForm;

        const handleDelete = (): void => {
          if (!selectedCustomer._id) {
            return;
          }
          setActiveModal({
            title: "Delete Customer",
            message: "Are you certain you wish to delete this customer? This operation cannot be undone.",
            cancel: () => setActiveModal(null),
            confirm: async (): Promise<void> => {
              setActiveModal(null);
              const notifications: INotifications = {
                dispatch: dispatch,
                infoMessage: "Deleting Selected Customer...",
                successMessage: "Customer Deleted",
                errorMessage: "Error: Unable To Delete Customer",
              };
              const response: any = await AmplifyAPIWrapper.Del(
                `/customer/${selectedCustomer._id}`,
                undefined,
                notifications
              );
              //This delete action provides no response when successful
              if (!response) {
                updateCustomerList(selectedCustomer, "delete");
                customerForm.setStatus({ success: true });
                customerForm.resetForm({ values: initialCustomerValues });
              } else {
                customerForm.setStatus({ success: false });
              }
              customerForm.setSubmitting(false);
            },
            confirmLabel: "Delete",
          });
        };

        return (
          <Form>
            <Grid container columnSpacing={3}>
              <Grid item xs={6}>
                <Typography>required fields</Typography>
                <FormikTextField label="Name" name="name" />
                <FormikTextField label="Details" name="details" multiline={true} minRows={3} maxRows={10} />
                <FormikSelectField
                  className="field selectfield customer_type"
                  name="customerType"
                  label="Customer Type"
                  getOptionLabel={(option: ICustomerType) =>
                    `${option.title} ${option.title !== option.description ? `(${option.description})` : ""}`
                  }
                  getOptionValue={(option: ICustomerType) => option._id}
                  options={customerTypeOptions.sort((a, b) => {
                    return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
                  })}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  isSearchable={true}
                  isClearable={false}
                  menuPosition="fixed"
                  hideSelectedValues={true}
                />
                <FormikSwitchField label="Active" name="active" />
              </Grid>
              <Grid item xs={3}>
                <CustomFieldsV2
                  schema={schema}
                  objectPath={customPath}
                  resource={PolicyResource.Customer}
                  displayHeader={true}
                />
              </Grid>
              <Grid item xs={12}>
                <ButtonGroup size="small" variant="contained">
                  {selectedCustomer?._id && (
                    <Button
                      color="error"
                      disabled={selectedCustomer === initialCustomerValues || isSubmitting}
                      onClick={handleDelete}
                    >
                      Delete
                    </Button>
                  )}
                  <Button
                    type="reset"
                    variant="contained"
                    color="warning"
                    disabled={(!dirty && isValid) || isSubmitting}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                  <Button color="success" disabled={isSubmitting || !isValid || !dirty} type="submit">
                    {selectedCustomer ? "Update" : "Create"}
                  </Button>
                </ButtonGroup>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
